import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import api from '../services/api';
import toast from 'react-hot-toast';

const AuthContext = createContext(null);

function getTokenExpiry(token) {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload.exp ? payload.exp * 1000 : null; // ms desde epoch
  } catch {
    return null;
  }
}

export function AuthProvider({ children }) {
  const [usuario, setUsuario] = useState(null);
  const [canais, setCanais] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const timersRef = useRef([]);

  const limparAvisosSessao = () => {
    timersRef.current.forEach(clearTimeout);
    timersRef.current = [];
  };

  const agendarAvisoSessao = (token) => {
    limparAvisosSessao();
    const expiryMs = getTokenExpiry(token);
    if (!expiryMs) return;
    const msRestante = expiryMs - Date.now();
    if (msRestante <= 0) return;

    const avisar = (mensagem) => {
      toast(mensagem, { icon: '⚠️', duration: 10000 });
    };

    const lead5min = 5 * 60 * 1000;
    if (msRestante > lead5min) {
      const t1 = setTimeout(
        () => avisar('Sua sessão irá expirar em 5 minutos. Salve seu trabalho e faça login novamente em breve.'),
        msRestante - lead5min
      );
      timersRef.current.push(t1);
    } else {
      avisar('Sua sessão irá expirar em breve. Faça login novamente em breve.');
    }

    const lead1min = 60 * 1000;
    if (msRestante > lead1min) {
      const t2 = setTimeout(
        () => avisar('Sua sessão irá expirar em 1 minuto!'),
        msRestante - lead1min
      );
      timersRef.current.push(t2);
    }
  };

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      api.defaults.headers.Authorization = `Bearer ${token}`;
      api.get('/auth/me')
        .then(({ data }) => {
          setUsuario(data.usuario);
          setCanais(data.canais);
          agendarAvisoSessao(token);
        })
        .catch(() => localStorage.removeItem('token'))
        .finally(() => setCarregando(false));
    } else {
      setCarregando(false);
    }
    return () => limparAvisosSessao();
  }, []);

  // Aplica as variáveis CSS do tema diretamente no :root
  useEffect(() => {
    const temas = {
      azul:     { cor: '#137aba', light: '#4fa8d8', rgb: '19,122,186' },
      verde:    { cor: '#0d9268', light: '#3ecf9a', rgb: '13,146,104' },
      roxo:     { cor: '#7c3aed', light: '#a78bfa', rgb: '124,58,237' },
      laranja:  { cor: '#ea580c', light: '#fb923c', rgb: '234,88,12' },
      vermelho: { cor: '#dc2626', light: '#f87171', rgb: '220,38,38' },
    };
    const tema = usuario?.tema || 'azul';
    const root = document.documentElement;
    if (tema.startsWith('custom_')) {
      const hex = '#' + tema.slice(7);
      root.style.setProperty('--tema-cor', hex);
      root.style.setProperty('--tema-cor-light', hex);
      root.style.setProperty('--tema-cor-rgb', parseInt(hex.slice(1,3),16)+','+parseInt(hex.slice(3,5),16)+','+parseInt(hex.slice(5,7),16));
    } else {
      const t = temas[tema] || temas.azul;
      root.style.setProperty('--tema-cor', t.cor);
      root.style.setProperty('--tema-cor-light', t.light);
      root.style.setProperty('--tema-cor-rgb', t.rgb);
    }
  }, [usuario?.tema]);

  const atualizarTema = async (tema) => {
    // Se for cor hex personalizada, aplicar direto no :root sem salvar no banco
    if (tema.startsWith('#')) {
      const root = document.documentElement;
      root.style.setProperty('--tema-cor', tema);
      // Gerar versão mais clara automaticamente
      root.style.setProperty('--tema-cor-light', tema);
      root.style.setProperty('--tema-cor-rgb', parseInt(tema.slice(1,3),16)+','+parseInt(tema.slice(3,5),16)+','+parseInt(tema.slice(5,7),16));
      setUsuario(prev => prev ? { ...prev, tema } : prev);
      try { await api.put('/usuarios/me', { tema: 'custom_' + tema.slice(1) }); } catch {}
      return;
    }
    await api.put('/usuarios/me', { tema });
    setUsuario(prev => prev ? { ...prev, tema } : prev);
  };

  const login = async (usuario, senha) => {
    const { data } = await api.post('/auth/login', { usuario, senha });
    localStorage.setItem('token', data.token);
    api.defaults.headers.Authorization = `Bearer ${data.token}`;
    setUsuario(data.usuario);
    setCanais(data.canais);
    agendarAvisoSessao(data.token);
    return data;
  };

  const logout = () => {
    limparAvisosSessao();
    localStorage.removeItem('token');
    delete api.defaults.headers.Authorization;
    setUsuario(null);
    setCanais([]);
  };

  return (
    <AuthContext.Provider value={{ usuario, canais, carregando, login, logout, atualizarTema }}>
      {children}
    </AuthContext.Provider>
  );
}
export const useAuth = () => useContext(AuthContext);
